import { getOpenOverlayElements } from './dom.helpers';

const TOOLTIP_POPPER_SELECTOR = '.MuiTooltip-popper';

let openMenuCount = 0;
let listenerUsers = 0;
const extraOverlayRoots = new Set<Element>();

/**
 * While `document.body.dataset.menuOpen` is set, theme CSS hides MUI hover
 * tooltips. Open menus use this so trigger tooltips cannot sit on top of the
 * menu. Ref-counted so nested menus can overlap.
 */
export const incrementOpenMenuTooltipSuppression = (): void => {
  openMenuCount++;
  document.body.dataset.menuOpen = 'true';
};

/**
 * Drops one {@link incrementOpenMenuTooltipSuppression} hold. Clears the body
 * flag when the last hold is released.
 */
export const decrementOpenMenuTooltipSuppression = (): void => {
  openMenuCount = Math.max(0, openMenuCount - 1);
  if (openMenuCount === 0) {
    delete document.body.dataset.menuOpen;
  }
};

/**
 * True when a hover tooltip may open for `target`. With no overlay roots,
 * every target is allowed. Otherwise only anchors inside a root (and the
 * tooltip popper itself) may show a tooltip.
 */
export const shouldAllowHoverTooltip = (
  target: EventTarget | null,
  roots: readonly Element[],
): boolean => {
  if (roots.length === 0) return true;
  if (!(target instanceof Element)) return true;
  if (target.closest(TOOLTIP_POPPER_SELECTOR)) return true;
  return roots.some((root) => root.contains(target));
};

/**
 * Overlay surfaces that may still show hover tooltips: registered custom
 * roots plus open MUI modal/popover/dialog roots.
 */
export const getActiveHoverTooltipRoots = (): Element[] => [
  ...extraOverlayRoots,
  ...getOpenOverlayElements(),
];

/**
 * Closes MUI hover tooltips whose anchor sits outside `roots`.
 */
export const closeHoverTooltipsOutside = (roots: readonly Element[]): void => {
  for (const element of document.querySelectorAll('[aria-describedby]')) {
    if (!(element instanceof HTMLElement)) continue;
    if (roots.some((root) => root.contains(element))) continue;
    const tooltipId = element.getAttribute('aria-describedby');
    if (!tooltipId) continue;
    const tooltip = document.getElementById(tooltipId);
    if (!tooltip?.classList.contains('MuiTooltip-popper')) continue;
    element.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
  }
};

const onMouseOverCapture = (event: Event): void => {
  const roots = getActiveHoverTooltipRoots();
  if (shouldAllowHoverTooltip(event.target, roots)) return;
  event.stopPropagation();
};

const acquireHoverTooltipListeners = (): void => {
  listenerUsers++;
  if (listenerUsers !== 1) return;
  document.addEventListener('mouseover', onMouseOverCapture, true);
};

const releaseHoverTooltipListeners = (): void => {
  listenerUsers = Math.max(0, listenerUsers - 1);
  if (listenerUsers !== 0) return;
  document.removeEventListener('mouseover', onMouseOverCapture, true);
};

/**
 * Lets hover tooltips inside `root` keep working while blocking them on the
 * rest of the page. Pair with {@link watchMuiOverlayHoverTooltips} so MUI
 * menus/modals get the same treatment without registering each one.
 *
 * @returns Unregister function.
 */
export const registerHoverTooltipOverlayRoot = (
  root: Element,
): (() => void) => {
  extraOverlayRoots.add(root);
  acquireHoverTooltipListeners();
  closeHoverTooltipsOutside(getActiveHoverTooltipRoots());
  return () => {
    extraOverlayRoots.delete(root);
    releaseHoverTooltipListeners();
  };
};

/**
 * Watches open MUI overlays (menu, popover, dialog) so background hover
 * tooltips cannot appear over them. Custom overlays still need
 * {@link registerHoverTooltipOverlayRoot}.
 *
 * @returns Unregister function.
 */
export const watchMuiOverlayHoverTooltips = (): (() => void) => {
  acquireHoverTooltipListeners();
  const observer = new MutationObserver(() => {
    const roots = getActiveHoverTooltipRoots();
    if (roots.length === 0) return;
    closeHoverTooltipsOutside(roots);
  });
  observer.observe(document.body, { childList: true });
  return () => {
    observer.disconnect();
    releaseHoverTooltipListeners();
  };
};
